export * from './types';

import { checkServerResponse } from '@core/utils';
import * as AuthQueryGen from '@generated/auth/@tanstack/react-query.gen';
import { client as AuthClient } from '@generated/auth/client.gen';
import { pathMapping as authPathMapping } from '@generated/auth/pathMapping';
import * as AuthSdkGen from '@generated/auth/sdk.gen';
import * as UnauthQueryGen from '@generated/unauth/@tanstack/react-query.gen';
import { client as UnauthClient } from '@generated/unauth/client.gen';
import { pathMapping as unauthPathMapping } from '@generated/unauth/pathMapping';
import * as UnauthSdkGen from '@generated/unauth/sdk.gen';
import { queryOptions } from '@tanstack/react-query';

import { COOKIE_OPTIONS, COOKIES_KEYS } from '@/constants/cookies';
import { ENV } from '@/constants/env';
import { cookies } from '@/utils/cookies';

import {
  isAccessTokenExpired,
  REFRESH_ENDPOINT,
  resolveServerAccessToken,
} from './serverAccessToken';
import { withNoStore } from './withNoStore';

// ─── Types ───────────────────────────────────────────────────────────────────

type PathMapping<
  M extends Record<string, string>,
  S,
  Suffix extends string = '',
> = {
  [
    K in keyof M as `${M[K] & string}${Suffix}` extends keyof S ? K : never
  ]: S[`${M[K] & string}${Suffix}` & keyof S];
};

type MakeOptional<F> = F extends (options: infer O) => infer R
  ? (options?: O) => R
  : F;

type PathMappingOptional<
  M extends Record<string, string>,
  S,
  Suffix extends string = '',
> = {
  [
    K in keyof M as `${M[K] & string}${Suffix}` extends keyof S ? K : never
  ]: MakeOptional<S[`${M[K] & string}${Suffix}` & keyof S]>;
};

type SdkResponse<F> = F extends (
  ...args: never[]
) => Promise<{ data?: infer R }>
  ? NonNullable<R>
  : unknown;

type SdkOptions<F> =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  F extends <_T extends boolean>(options: infer O) => unknown
    ? O
    : F extends (options: infer O) => unknown
      ? O
      : F extends (options?: infer O) => unknown
        ? O
        : never;

type PathToPostQuery<M extends Record<string, string>, S> = {
  [
    K in keyof M as K extends `post:${string}`
      ? M[K] extends keyof S
        ? K
        : never
      : never
  ]: (options?: SdkOptions<S[M[K] & keyof S]>) => {
    queryKey: readonly unknown[];
    queryFn: () => Promise<SdkResponse<S[M[K] & keyof S]>>;
  };
};

type SdkFn = (options: {
  signal?: AbortSignal;
  throwOnError?: boolean;
}) => Promise<{ data: unknown }>;

// ─── Core Factory ────────────────────────────────────────────────────────────

function mapPaths<
  M extends Record<string, string>,
  S extends Record<string, unknown>,
  Suffix extends string = '',
>(mapping: M, source: S, suffix?: Suffix): PathMapping<M, S, Suffix> {
  const result: Record<string, unknown> = {};
  for (const [path, operationId] of Object.entries(mapping)) {
    const key = operationId + (suffix ?? '');
    if (key in source) result[path] = source[key];
  }

  return result as PathMapping<M, S, Suffix>;
}

function mapPathsOptional<
  M extends Record<string, string>,
  S extends Record<string, unknown>,
  Suffix extends string,
>(
  mapping: M,
  source: S,
  suffix: Suffix,
  transform: (fn: unknown) => unknown,
): PathMappingOptional<M, S, Suffix> {
  const result: Record<string, unknown> = {};
  for (const [path, operationId] of Object.entries(mapping)) {
    const key = operationId + suffix;
    if (key in source) result[path] = transform(source[key]);
  }

  return result as PathMappingOptional<M, S, Suffix>;
}

function createQueryOptionsWithPost<
  M extends Record<string, string>,
  Q extends Record<string, unknown>,
  S extends Record<string, unknown>,
>(
  mapping: M,
  queryModule: Q,
  sdk: S,
): PathMapping<M, Q, 'Options'> & PathToPostQuery<M, S> {
  const result: Record<string, unknown> = mapPaths(
    mapping,
    queryModule,
    'Options',
  );

  for (const [path, operationId] of Object.entries(mapping)) {
    if (!result[path] && path.startsWith('post:') && operationId in sdk) {
      result[path] = (options?: Record<string, unknown>) =>
        queryOptions({
          queryKey: [{ _id: operationId, ...options }],
          queryFn: async ({ signal }) =>
            (
              await (sdk[operationId] as SdkFn)({
                ...options,
                signal,
                throwOnError: true,
              })
            ).data,
        });
    }
  }

  return result as PathMapping<M, Q, 'Options'> & PathToPostQuery<M, S>;
}

// ─── Auth Expired Handler ─────────────────────────────────────────────────────

let authExpiredHandler: (() => void) | null = null;

export const onAuthExpired = (handler: () => void) => {
  authExpiredHandler = handler;
};

const triggerAuthExpired = () => {
  authExpiredHandler?.();
};

// ─── 401 Token Refresh ───────────────────────────────────────────────────────

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const refreshTokens = async (): Promise<boolean> => {
  const refreshToken = cookies.get(COOKIES_KEYS.REFRESH_TOKEN);
  if (!refreshToken) return false;

  try {
    const res = await fetch(
      `${ENV.NEXT_PUBLIC_API_DOMAIN}${REFRESH_ENDPOINT}?refreshToken=${encodeURIComponent(refreshToken)}`,
    );
    const data = await res.json();

    if (data.return_code !== 0) return false;

    const newAccessToken = data.context?.accessToken;
    const newRefreshToken = data.context?.refreshToken;

    if (!newAccessToken || !newRefreshToken) return false;

    cookies.set(COOKIES_KEYS.ACCESS_TOKEN, newAccessToken, COOKIE_OPTIONS);
    cookies.set(COOKIES_KEYS.REFRESH_TOKEN, newRefreshToken, COOKIE_OPTIONS);
    AuthClient.setConfig({ auth: newAccessToken });

    return true;
  } catch {
    return false;
  }
};

const tryRefreshTokens = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshTokens().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
};

// ─── Client Setup ────────────────────────────────────────────────────────────

AuthClient.setConfig({ baseUrl: ENV.NEXT_PUBLIC_API_DOMAIN });
UnauthClient.setConfig({ baseUrl: ENV.NEXT_PUBLIC_API_DOMAIN });

// Request: SSR에서 쿠키 토큰 주입 (백엔드는 Bearer 스킴만 인증한다 — 원값 주입 금지)
// 만료 토큰이면 선제 리프레시로 교체해 SSR 프리페치가 401로 무산되지 않게 한다.
AuthClient.interceptors.request.use(async (request) => {
  if (typeof window === 'undefined') {
    try {
      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();
      let accessToken = cookieStore.get(COOKIES_KEYS.ACCESS_TOKEN)?.value;
      const refreshToken = cookieStore.get(COOKIES_KEYS.REFRESH_TOKEN)?.value;

      if (
        refreshToken &&
        (!accessToken || isAccessTokenExpired(accessToken, Date.now()))
      ) {
        accessToken =
          (await resolveServerAccessToken(refreshToken, Date.now())) ??
          accessToken;
      }

      if (accessToken && !request.headers.has('Authorization')) {
        request.headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } catch {
      // SSR 쿠키 접근 실패 무시
    }

    return withNoStore(request);
  }

  return request;
});

// Request: SSR fetch에 client IP·User-Agent 전달 (미전달 시 백엔드에 NAT IP·node로 기록됨)
const CLIENT_FORWARDED_HEADERS = [
  'x-forwarded-for',
  'x-real-ip',
  'user-agent',
] as const;

const forwardClientHeaders = async (request: Request) => {
  if (typeof window !== 'undefined') return request;

  try {
    const { headers } = await import('next/headers');
    const incomingHeaders = await headers();

    for (const headerName of CLIENT_FORWARDED_HEADERS) {
      const value = incomingHeaders.get(headerName);
      if (value) {
        request.headers.set(headerName, value);
      }
    }
  } catch {
    // 정적 생성 등 요청 컨텍스트가 없으면 무시
  }

  return request;
};

AuthClient.interceptors.request.use(forwardClientHeaders);
UnauthClient.interceptors.request.use(forwardClientHeaders);

const responseInterceptor = async (res: Response) => {
  await checkServerResponse(res);

  return res;
};

//* 401 발생 시 토큰 갱신 후 재시도
const authResponseInterceptor = async (
  res: Response,
  request: Request,
  options: { fetch?: typeof fetch },
) => {
  //* 리프레쉬 엔드포인트는 재시도하지 않음
  if (res.status === 401 && !request.url.includes(REFRESH_ENDPOINT)) {
    const refreshed = await tryRefreshTokens();

    if (refreshed) {
      //* POST 요청 일 경우 body 가 한 번 소비(consumed)되므로 재시도 X
      //* 전역적으로 재시도를 구현해야만 한다면 아래처럼 기존 request 객체를 참조하도록 하지 않고, request 내부 값을 사용하여 직접 새로운 request 객체를 생성하여 사용해야 함
      if (request.method === 'POST') {
        return res;
      }

      const newToken = cookies.get(COOKIES_KEYS.ACCESS_TOKEN);

      if (!newToken) {
        return res;
      }

      const newRequest = new Request(request, {
        headers: new Headers(request.headers),
      });
      newRequest.headers.set('Authorization', `Bearer ${newToken}`);

      const fetchFn = options.fetch ?? fetch;

      return fetchFn(newRequest);
    }

    triggerAuthExpired();

    return res;
  }

  await checkServerResponse(res);

  return res;
};

AuthClient.interceptors.response.use(authResponseInterceptor);
UnauthClient.interceptors.response.use(responseInterceptor);

// ─── API Exports ─────────────────────────────────────────────────────────────

export const AuthApi = mapPaths(authPathMapping, AuthSdkGen);
export const UnauthApi = mapPaths(unauthPathMapping, UnauthSdkGen);

export const AuthQueryOptions = createQueryOptionsWithPost(
  authPathMapping,
  AuthQueryGen,
  AuthSdkGen,
);
export const UnauthQueryOptions = createQueryOptionsWithPost(
  unauthPathMapping,
  UnauthQueryGen,
  UnauthSdkGen,
);

export const AuthInfiniteQueryOptions = mapPaths(
  authPathMapping,
  AuthQueryGen,
  'InfiniteOptions',
);
export const UnauthInfiniteQueryOptions = mapPaths(
  unauthPathMapping,
  UnauthQueryGen,
  'InfiniteOptions',
);

export const AuthMutation = mapPaths(authPathMapping, AuthQueryGen, 'Mutation');
export const UnauthMutation = mapPaths(
  unauthPathMapping,
  UnauthQueryGen,
  'Mutation',
);

const wrapOptional = (fn: unknown) => (options?: unknown) =>
  (fn as (options?: unknown) => unknown)(options ?? {});
export const AuthQueryKey = mapPathsOptional(
  authPathMapping,
  AuthQueryGen,
  'QueryKey',
  wrapOptional,
);
export const AuthInfiniteQueryKey = mapPathsOptional(
  authPathMapping,
  AuthQueryGen,
  'InfiniteQueryKey',
  wrapOptional,
);
export const UnauthQueryKey = mapPathsOptional(
  unauthPathMapping,
  UnauthQueryGen,
  'QueryKey',
  wrapOptional,
);
export { AuthSdkGen as AuthSdk, UnauthSdkGen as UnauthSdk };
export { AuthClient };
